import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, Save, Wand2, Lock, PhoneCall, ShieldCheck } from "lucide-react";

const STAGE_LABEL = {
  friendly_reminder: "1st Notice · Friendly Reminder",
  second_notice:     "2nd Notice · Registered",
  demand_letter:     "3rd Notice · 10-Day Demand",
  intent_to_lien:    "Final Notice · Intent to Lien",
};

export default function CollectionsPolicy() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [policy, setPolicy] = useState(null);
  const [plan, setPlan] = useState("standard");
  const [sub, setSub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ governing_doc_text: "", state: "Louisiana" });
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/collections-policy"), api.get("/subscription")]);
      setPolicy(p.data);
      setSub(s.data);
      setPlan(s.data.plan_id || "standard");
    } catch (err) {
      console.error("policy load failed:", err);
      toast.error(err.response?.data?.detail || "Could not load policy");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canAI = plan === "elite";
  const hasCourtesyCalls = sub?.active_addons?.some((a) => a.addon_id === "ai_courtesy_calls" && ["trial", "active"].includes(a.status));

  const updateStage = (idx, key, value) => {
    const stages = [...policy.stages];
    stages[idx] = { ...stages[idx], [key]: key === "stage" ? value : Number(value) };
    setPolicy({ ...policy, stages });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        late_fee_amount: Number(policy.late_fee_amount),
        late_fee_at_days: Number(policy.late_fee_at_days),
        phone_calls_enabled: !!policy.phone_calls_enabled,
        stages: policy.stages,
        gov_doc_ref: policy.gov_doc_ref,
      };
      const { data } = await api.post("/collections-policy", body);
      setPolicy(data);
      toast.success("Collections policy saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const aiGenerate = async (e) => {
    e.preventDefault();
    setAiBusy(true);
    try {
      const { data } = await api.post("/collections-policy/ai-generate", aiForm);
      setPolicy(data);
      toast.success("AI Saint aligned your policy to the governing doc");
      setAiOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not align");
    } finally { setAiBusy(false); }
  };

  if (!isBoard) {
    return <div className="hoa-card p-10 text-center" data-testid="policy-forbidden">Board-only view</div>;
  }
  if (!policy) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="collections-policy-page">
      <Link to="/delinquency" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="policy-back-link">
        <ChevronLeft className="w-4 h-4" /> Back to delinquency
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <ShieldCheck className="w-4 h-4" /> Governing collections policy
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Escalation policy</h1>
          <p className="text-foreground/70 mt-1 max-w-2xl">
            Notices escalate on this schedule. Higher tiers can align to your governing docs automatically — lower tiers set it manually.
          </p>
        </div>
        <div className="flex gap-2">
          {canAI ? (
            <Dialog open={aiOpen} onOpenChange={setAiOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="ai-align-btn">
                  <Wand2 className="w-4 h-4 mr-1.5" /> AI-align to governing doc
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Align policy to your governing docs</DialogTitle></DialogHeader>
                <form onSubmit={aiGenerate} className="space-y-3" data-testid="ai-align-form">
                  <div>
                    <Label>Paste governing-doc excerpt (CC&Rs / Bylaws / Rules — sections on assessments & collections)</Label>
                    <Textarea required minLength={200} value={aiForm.governing_doc_text} onChange={(e) => setAiForm({ ...aiForm, governing_doc_text: e.target.value })} className="mt-1.5 min-h-[220px] font-mono text-xs" placeholder="e.g. ARTICLE VI § 6.3 — ASSESSMENTS. Any assessment not paid within fifteen (15) days after due date shall bear a late fee of $25… After thirty (30) days, the Board shall send a second written notice via certified mail…" data-testid="ai-doc-input" />
                    <div className="text-xs text-muted-foreground mt-1">Minimum 200 characters. AI Saint uses only what you paste — nothing else.</div>
                  </div>
                  <div>
                    <Label>State (for statute context)</Label>
                    <Input value={aiForm.state} onChange={(e) => setAiForm({ ...aiForm, state: e.target.value })} className="mt-1.5 h-11" data-testid="ai-state-input" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={aiBusy} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="ai-align-submit">
                      {aiBusy ? "Aligning…" : "Generate aligned policy"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <Link to="/subscription"><Button variant="outline" className="rounded-full" data-testid="upgrade-policy-btn">
              <Lock className="w-4 h-4 mr-1.5" /> Upgrade to Elite for AI-align
            </Button></Link>
          )}
        </div>
      </div>

      {policy.ai_generated && (
        <div className="hoa-card royal-ring p-4 flex items-start gap-3" data-testid="ai-generated-banner">
          <Sparkles className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-gold">AI-aligned policy</div>
            <div className="text-foreground/80">This policy was generated by AI Saint from your governing docs on {new Date(policy.updated_at).toLocaleDateString()} by {policy.updated_by}. Every letter will cite matching sections.</div>
            {policy.gov_doc_ref && <div className="text-xs text-muted-foreground mt-1">Cited: {policy.gov_doc_ref}</div>}
          </div>
        </div>
      )}

      <div className="hoa-card p-6">
        <h2 className="text-lg font-semibold mb-3">Late-fee policy</h2>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Standard late fee ($)</Label>
            <Input type="number" step="0.01" value={policy.late_fee_amount} onChange={(e) => setPolicy({ ...policy, late_fee_amount: e.target.value })} className="mt-1.5 h-11" data-testid="latefee-amount-input" />
          </div>
          <div>
            <Label>Applied after (days)</Label>
            <Input type="number" value={policy.late_fee_at_days} onChange={(e) => setPolicy({ ...policy, late_fee_at_days: e.target.value })} className="mt-1.5 h-11" data-testid="latefee-days-input" />
          </div>
          <div>
            <Label>Governing-doc citation</Label>
            <Input value={policy.gov_doc_ref || ""} onChange={(e) => setPolicy({ ...policy, gov_doc_ref: e.target.value })} className="mt-1.5 h-11" placeholder="CC&Rs § 6.3" data-testid="gov-doc-ref-input" />
          </div>
        </div>
      </div>

      <div className="hoa-card p-6">
        <h2 className="text-lg font-semibold mb-4">Escalation stages</h2>
        <div className="space-y-2">
          {policy.stages.map((s, i) => (
            <div key={`${s.stage}-${i}`} className="grid grid-cols-12 gap-3 items-center p-3 rounded-lg bg-accent/20 border border-border" data-testid={`policy-stage-${s.seq}`}>
              <div className="col-span-1 text-center">
                <div className="w-10 h-10 rounded-full bg-gold text-secondary-foreground flex items-center justify-center font-mono font-bold">{s.seq}</div>
              </div>
              <div className="col-span-4">
                <div className="font-semibold text-gold">{STAGE_LABEL[s.stage] || s.stage}</div>
                <div className="text-xs text-muted-foreground">{s.stage}</div>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Days past due</Label>
                <Input type="number" value={s.days_after_due} onChange={(e) => updateStage(i, "days_after_due", e.target.value)} className="mt-1 h-10" data-testid={`stage-days-${s.seq}`} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Cure days</Label>
                <Input type="number" value={s.cure_days} onChange={(e) => updateStage(i, "cure_days", e.target.value)} className="mt-1 h-10" data-testid={`stage-cure-${s.seq}`} />
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Late fee ($)</Label>
                <Input type="number" step="0.01" value={s.late_fee} onChange={(e) => updateStage(i, "late_fee", e.target.value)} className="mt-1 h-10" data-testid={`stage-fee-${s.seq}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={`hoa-card p-6 ${hasCourtesyCalls ? "royal-ring" : ""}`} data-testid="courtesy-calls-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gold text-secondary-foreground flex items-center justify-center">
              <PhoneCall className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">AI Courtesy Calls</h3>
              <p className="text-sm text-foreground/70 max-w-lg">
                Have AI Saint place TCPA-compliant reminder calls after 1st and 2nd notices — with voicemail drop, transcripts, and auto-skip for opt-outs.
              </p>
              {hasCourtesyCalls ? (
                <label className="flex items-center gap-2 mt-3 text-sm" data-testid="courtesy-toggle">
                  <input
                    type="checkbox"
                    checked={!!policy.phone_calls_enabled}
                    onChange={(e) => setPolicy({ ...policy, phone_calls_enabled: e.target.checked })}
                    className="accent-[hsl(var(--gold))]"
                  />
                  <span className="text-gold font-medium">Enable AI courtesy calls in escalations</span>
                </label>
              ) : (
                <div className="text-xs text-muted-foreground mt-2 italic">Add-on required · $79/mo (14-day trial)</div>
              )}
            </div>
          </div>
          {!hasCourtesyCalls && (
            <Link to="/subscription">
              <Button variant="outline" className="rounded-full" data-testid="add-courtesy-btn">Add to plan</Button>
            </Link>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button onClick={save} disabled={saving} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="save-policy-btn">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save policy"}
        </Button>
      </div>
    </div>
  );
}
